import { useTranslations } from '@vocab/react';
import { ButtonLink, IconRocket, IconTick, Stack } from 'braid-design-system';
import { parseISO, isBefore, add } from 'date-fns';

import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import { TrackOnMount } from 'src/libs/Analytics';
import {
  DotPointItem,
  ImageModal,
  ImageModalContent,
  TextGroup,
} from 'src/libs/ImageModal';

import { FreemiumImage } from '../FreemiumImage';

import translations from './.vocab';

const shouldShowUrgentHire = ({
  jobCreatedDate,
}: {
  jobCreatedDate: string;
}): boolean => {
  const jobIsoDate = parseISO(jobCreatedDate);

  const threeDaysAfter = add(jobIsoDate, { days: 3 });

  return Boolean(isBefore(Date.now(), threeDaysAfter));
};
interface FreemiumUrgentHireModalProps {
  link: string;
  jobCreatedDate: string;
  trackButtonPressed: () => void;
  trackModalDisplayed: () => void;
  trackModalDismissed: () => void;
}

export const FreemiumUrgentHireModal = ({
  link,
  jobCreatedDate,
  trackModalDisplayed,
  trackButtonPressed,
  trackModalDismissed,
}: FreemiumUrgentHireModalProps) => {
  const { t } = useTranslations(translations);
  const { getFreemiumUrgentHireModalStatus, hideFreemiumUrgentHireModal } =
    useUserActionStatus('freemium-urgent-hire');

  const showModal =
    shouldShowUrgentHire({ jobCreatedDate }) &&
    getFreemiumUrgentHireModalStatus() === 'visible';

  if (!showModal) {
    return null;
  }

  const closeModal = () => {
    trackModalDismissed();
    hideFreemiumUrgentHireModal();
  };
  const titleId = 'FreemiumUrgentHireModal';
  return (
    <TrackOnMount onMount={trackModalDisplayed}>
      <ImageModal
        isOpen={showModal}
        close={closeModal}
        image={<FreemiumImage />}
        titleId={titleId}
      >
        <ImageModalContent
          titleId={titleId}
          heading={t('Need to fill this role fast?')}
          subheading={t(
            'Paid ads work better for urgent hiring. Upgrade and extend your ad for 30 days.',
          )}
          actions={
            <ButtonLink
              size="small"
              href={link}
              icon={<IconRocket />}
              iconPosition="trailing"
              tone="brandAccent"
              onClick={trackButtonPressed}
              target="_blank"
            >
              {t('Upgrade now')}
            </ButtonLink>
          }
          content={
            <Stack space="medium">
              <DotPointItem
                icon={<IconTick tone="positive" />}
                content={
                  <TextGroup
                    heading={t('Increase visibility')}
                    description={t('Appear more often in search results.')}
                  />
                }
              />
              <DotPointItem
                icon={<IconTick tone="positive" />}
                content={
                  <TextGroup
                    heading={t('Reach more candidates')}
                    description={t(
                      'Be featured in candidate emails and notifications.',
                    )}
                  />
                }
              />
              <DotPointItem
                icon={<IconTick tone="positive" />}
                content={
                  <TextGroup
                    heading={t('Get AI-powered candidate matches')}
                    description={t(
                      'Access a curated list of candidates who match your requirements.',
                    )}
                  />
                }
              />
            </Stack>
          }
        />
      </ImageModal>
    </TrackOnMount>
  );
};
